import { Injectable } from '@angular/core';
import { ApiService } from '../api-service/api.service';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, BehaviorSubject, timer } from 'rxjs';
import { map, catchError, filter } from 'rxjs/operators';

export interface PopupData {
  id: string;
  title: string;
  title_fr: string;
  title_ar: string;
  image_url: string;
  document_url: string;
  button_text: string;
  display_location: string;
  display_frequency: string;
  display_count: number;
  auto_close: number;
}

export interface PopupApiResponse {
  success: boolean;
  data: {
    popup: PopupData;
  };
}

@Injectable({
  providedIn: 'root'
})
export class PopupService {
  private popupSubject = new BehaviorSubject<PopupData | null>(null);
  public popup$ = this.popupSubject.asObservable();

  private readonly POPUP_STORAGE_PREFIX = 'popup_';
  private popupData: PopupData | null = null;
  private routerSubscription: any;
  private hasInitialized = false; // Track if we've fetched data on this session

  constructor(
    private apiService: ApiService,
    private router: Router
  ) {
    this.initializeRouteListener();
  }

  /**
   * Initialize router listener to check for popups on navigation
   */
  private initializeRouteListener(): void {
    // Listen for navigation end events
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Check for popup after navigation with a delay
      setTimeout(() => {
        this.checkPopupForCurrentRoute();
      }, 500);
    });
  }

  /**
   * Check if popup should be shown for current route (using cached data only)
   */
  private checkPopupForCurrentRoute(): void {
    console.log('Navigation detected - using cached data only');

    if (this.popupData) {
      // Use cached popup data, check location AND frequency
      if (this.shouldShowOnCurrentPage(this.popupData.display_location) && this.shouldDisplayPopup()) {
        console.log('Route check passed - showing cached popup');
        this.popupSubject.next(this.popupData);
        this.markAsShown(this.popupData.id);
      } else {
        console.log('Route or frequency check failed - hiding popup');
        this.popupSubject.next(null);
      }
    } else {
      // No cached data available, don't show popup on navigation
      console.log('No cached popup data available for navigation');
      this.popupSubject.next(null);
    }
  }

  /**
   * Fetch active popup from API (without auto-showing)
   */
  fetchActivePopup(): Observable<PopupData | null> {
    return this.apiService.get('wpapis/active-popup').pipe(
      map((response: PopupApiResponse) => {
        if (response.success && response.data?.popup) {
          // Don't auto-emit here - let showPopupIfNeeded handle frequency checks first
          return response.data.popup;
        }
        return null;
      }),
      catchError((error) => {
        console.error('Error fetching popup:', error);
        return [null];
      })
    );
  }

  /**
   * Check if popup should be displayed based on frequency logic
   * Matches the JavaScript logic from popup-manager.php
   */
  shouldDisplayPopup(): boolean {
    if (!this.popupData) {
      console.log('No popup data available for frequency check');
      return false;
    }

    const popupId = this.popupData.id;
    const frequency = this.popupData.display_frequency;
    const maxCount = this.popupData.display_count || 0;
    const now = new Date();
    const today = now.toDateString();

    console.log(`Frequency check - ID: ${popupId}, Frequency: ${frequency}, MaxCount: ${maxCount}`);

    switch(frequency) {
      case 'once_per_session':
        const sessionKey = this.getStorageKey(popupId, 'session');
        const sessionValue = sessionStorage.getItem(sessionKey);
        console.log(`Session check - Key: ${sessionKey}, Value: ${sessionValue}`);
        return !sessionValue;

      case 'once_per_day':
        const dayKey = this.getStorageKey(popupId, 'day');
        const lastDay = localStorage.getItem(dayKey);
        console.log(`Day check - Key: ${dayKey}, LastDay: ${lastDay}, Today: ${today}`);
        return lastDay !== today;

      case 'limited_session':
        const sessionCountKey = this.getStorageKey(popupId, 'session_count');
        const sessionCount = parseInt(sessionStorage.getItem(sessionCountKey) || '0');
        console.log(`Limited session check - Key: ${sessionCountKey}, Count: ${sessionCount}, Max: ${maxCount}`);
        return sessionCount < maxCount;

      case 'limited_day':
        const dayDataKey = this.getStorageKey(popupId, 'day_data');
        const dayData = localStorage.getItem(dayDataKey);
        console.log(`Limited day check - Key: ${dayDataKey}, Data: ${dayData}`);

        if (dayData) {
          try {
            const parsedData = JSON.parse(dayData);
            if (parsedData.date === today) {
              console.log(`Same day - Count: ${parsedData.count}, Max: ${maxCount}`);
              return parsedData.count < maxCount;
            }
          } catch (e) {
            console.warn('Error parsing day data:', e);
          }
        }
        console.log('No day data or new day - allowing popup');
        return true;

      case 'always':
      default:
        console.log('Always frequency - showing popup');
        return true;
    }
  }

  /**
   * Generate storage key for popup tracking
   */
  private getStorageKey(popupId: string, type: string): string {
    return `${this.POPUP_STORAGE_PREFIX}${popupId}_${type}`;
  }

  /**
   * Show the popup if conditions are met (only fetches API on first call)
   */
  showPopupIfNeeded(): void {
    // Only fetch from API if we haven't initialized yet (page refresh)
    if (!this.hasInitialized) {
      console.log('First initialization - fetching from API');
      this.hasInitialized = true;

      this.fetchActivePopup().subscribe((popup) => {
        if (popup) {
          // Set popup data first, then check frequency
          this.popupData = popup;

          console.log('Popup data received from API:', popup);
          console.log('Checking frequency for:', popup.display_frequency);

          if (this.shouldDisplayPopup()) {
            console.log('Frequency check passed - showing popup');
            // Emit popup data to component to show it
            this.popupSubject.next(popup);
            // Mark as shown AFTER emitting (when popup actually shows)
            this.markAsShown(popup.id);
          } else {
            console.log('Frequency check failed - not showing popup');
            // Ensure popup is hidden by emitting null
            this.popupSubject.next(null);
          }
        } else {
          console.log('No popup data received from API');
          // No popup data, ensure popup is hidden
          this.popupSubject.next(null);
        }
      });
    } else {
      // Use cached data for subsequent calls
      console.log('Using cached popup data - no API call');

      if (this.popupData) {
        if (this.shouldDisplayPopup()) {
          console.log('Cached frequency check passed - showing popup');
          this.popupSubject.next(this.popupData);
          this.markAsShown(this.popupData.id);
        } else {
          console.log('Cached frequency check failed - not showing popup');
          this.popupSubject.next(null);
        }
      } else {
        console.log('No cached popup data available');
        this.popupSubject.next(null);
      }
    }
  }

  /**
   * Mark popup as shown for frequency tracking
   * Matches the JavaScript logic from popup-manager.php
   */
  private markAsShown(popupId: string): void {
    if (!this.popupData) return;

    const frequency = this.popupData.display_frequency;
    const now = new Date();
    const today = now.toDateString();

    console.log(`Marking popup as shown - ID: ${popupId}, Frequency: ${frequency}`);

    switch(frequency) {
      case 'once_per_session':
        const sessionKey = this.getStorageKey(popupId, 'session');
        sessionStorage.setItem(sessionKey, '1');
        console.log(`Marked session - Key: ${sessionKey}, Value: 1`);
        break;

      case 'once_per_day':
        const dayKey = this.getStorageKey(popupId, 'day');
        localStorage.setItem(dayKey, today);
        console.log(`Marked day - Key: ${dayKey}, Value: ${today}`);
        break;

      case 'limited_session':
        const sessionCountKey = this.getStorageKey(popupId, 'session_count');
        const sessionCount = parseInt(sessionStorage.getItem(sessionCountKey) || '0');
        const newSessionCount = sessionCount + 1;
        sessionStorage.setItem(sessionCountKey, newSessionCount.toString());
        console.log(`Updated session count - Key: ${sessionCountKey}, Old: ${sessionCount}, New: ${newSessionCount}`);
        break;

      case 'limited_day':
        const dayDataKey = this.getStorageKey(popupId, 'day_data');
        const dayData = localStorage.getItem(dayDataKey);
        let newCount = 1;

        if (dayData) {
          try {
            const parsedData = JSON.parse(dayData);
            if (parsedData.date === today) {
              newCount = parsedData.count + 1;
            }
          } catch (e) {
            console.warn('Error parsing day data:', e);
          }
        }

        const newDayData = JSON.stringify({
          date: today,
          count: newCount
        });
        localStorage.setItem(dayDataKey, newDayData);
        console.log(`Updated day count - Key: ${dayDataKey}, Data: ${newDayData}`);
        break;

      // For 'always' frequency, we don't need to track
      case 'always':
      default:
        console.log('Always frequency - no tracking needed');
        break;
    }
  }

  /**
   * Hide the popup
   */
  hidePopup(): void {
    this.popupSubject.next(null);
  }

  /**
   * Get current language (French/Arabic support)
   */
  getCurrentLanguage(): string {
    // Check for language preference from localStorage or default to 'fr'
    return localStorage.getItem('selectedLanguage') || 'fr';
  }

  /**
   * Get popup content based on current language
   */
  getLocalizedContent(popup: PopupData): { title: string; buttonText: string } {
    const currentLang = this.getCurrentLanguage();

    return {
      title: (currentLang === 'ar' && popup.title_ar) ? popup.title_ar : popup.title_fr,
      buttonText: popup.button_text // Assuming button text is already localized from API
    };
  }

  /**
   * Check if current page should show popup based on display_location
   */
  shouldShowOnCurrentPage(displayLocation: string): boolean {
    const currentUrl = window.location.pathname;

    switch (displayLocation) {
      case 'home':
        // Only show on main dashboard page
        return currentUrl === '/' || currentUrl === '/dashboard' || currentUrl.includes('/dashboard');
      case 'all':
        return true;
      default:
        // Default to home behavior - only show on dashboard
        return currentUrl === '/' || currentUrl === '/dashboard' || currentUrl.includes('/dashboard');
    }
  }

  /**
   * Force refresh popup data from API (useful for admin/testing)
   */
  refreshPopupData(): void {
    console.log('Forcing popup data refresh');
    this.hasInitialized = false;
    this.popupData = null;
    this.showPopupIfNeeded();
  }

  /**
   * Clean up subscriptions
   */
  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}